<template>
  <modal class="style-modal-wide" @close-modal="close">
    <main class="two-fa-verify mt-1">
      <div class="container alignment">
        <div class="verify-header">
          <div class="verify-header__badge">
            <font-awesome-icon :icon="faShieldHalved" />
          </div>
          <h2 class="verify-header__title my-0">Two-Factor Verification</h2>
        </div>

        <!-- Method-specific instruction. The backend tells us which factor is
             active (`method`); the user can't pick — they prove whatever they
             have enrolled. -->
        <p class="instruction my-0 pb-3">
          <template v-if="method === 'totp'">
            Enter the 6-digit code from your authenticator app.
          </template>
          <template v-else>
            Enter the 6-digit code we sent to {{ maskedEmail }}.
          </template>
        </p>

        <div class="code-row py-2">
          <input
            v-model="code"
            class="codeInput"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            placeholder="000000"
            @keyup.enter="submit"
          />
        </div>

        <!-- Email codes can be resent (rate-limited); TOTP codes can't (they're
             generated on the user's device). -->
        <p v-if="method !== 'totp'" class="resend my-0 pb-2">
          Didn't get it?
          <span
            class="resend-link"
            :class="{ disabled: resendDisabled }"
            @click="resend"
          >
            {{ resendLabel }}
          </span>
        </p>

        <p v-if="error" class="error my-0 pb-2">{{ error }}</p>

        <div class="d-flex justify-content-center mt-2 p-0">
          <button
            :disabled="!canSubmit"
            type="submit"
            class="btn btn-primary mb-2 createButton bold"
            @click="submit"
          >
            <b>{{ submitting ? 'Verifying…' : 'Verify & Submit' }}</b>
          </button>
        </div>
      </div>
    </main>
  </modal>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons/faShieldHalved';
import Modal from '@/components/global/WideModal.vue';
import { useUserStore } from '@/stores/user';
import { useTwoFactor } from '@/composables/useTwoFactor';
import { PostHogUtils } from '@/core/PostHogUtils';

const props = defineProps({
  // The pending verification request to satisfy. Both come from the gate
  // response (e.g. cashier/withdrawal -> { requestId, method }).
  requestId: { type: String, required: true },
  method: { type: String, default: 'email' }, // 'email' | 'totp'
});

// verified — the proof landed and the backend ran the callback (e.g. submitted
//             the withdrawal). Parent should treat this as success.
// closeModal — user dismissed without verifying.
const emit = defineEmits(['verified', 'closeModal']);

const userStore = useUserStore();
const { verify, issueCode } = useTwoFactor();

const code = ref('');
const submitting = ref(false);
const resending = ref(false);
const error = ref('');

// Resend throttle: per-click cooldown + hard cap so codes can't be spammed.
const RESEND_COOLDOWN_BASE = 30; // seconds
const RESEND_MAX = 4;
const resendCount = ref(0);
const resendCooldown = ref(0);
let resendTimer = null;

const resendDisabled = computed(
  () => resending.value || resendCooldown.value > 0 || resendCount.value >= RESEND_MAX,
);

const resendLabel = computed(() => {
  if (resending.value) return 'Sending…';
  if (resendCount.value >= RESEND_MAX) return 'Resend limit reached';
  if (resendCooldown.value > 0) return `Resend in ${resendCooldown.value}s`;
  return 'Resend code';
});

function startResendCooldown() {
  resendCount.value += 1;
  resendCooldown.value = RESEND_COOLDOWN_BASE * resendCount.value;
  if (resendTimer) clearInterval(resendTimer);
  resendTimer = setInterval(() => {
    resendCooldown.value -= 1;
    if (resendCooldown.value <= 0) {
      clearInterval(resendTimer);
      resendTimer = null;
    }
  }, 1000);
}

onUnmounted(() => {
  if (resendTimer) clearInterval(resendTimer);
});

const canSubmit = computed(
  () => !submitting.value && /^\d{6}$/.test(code.value.trim()),
);

const maskedEmail = computed(() => {
  const email = userStore.user?.userEmail || '';
  const [name, domain] = email.split('@');
  if (!name || !domain) return email;
  const head = name.slice(0, 2);
  return `${head}${'*'.repeat(Math.max(name.length - 2, 1))}@${domain}`;
});

function close() {
  emit('closeModal');
}

async function submit() {
  if (!canSubmit.value) return;
  submitting.value = true;
  error.value = '';
  PostHogUtils.capture('twofa_verify_submitted', { method: props.method });
  try {
    const res = await verify({
      requestId: props.requestId,
      proof: code.value.trim(),
    });
    // verify resolves only on a successful proof; the callback (executeWithdrawal)
    // has already run server-side. Hand the result back to the parent.
    PostHogUtils.capture('twofa_verify_result', {
      outcome: 'success',
      method: props.method,
    });
    emit('verified', res);
  } catch (e) {
    PostHogUtils.capture('twofa_verify_result', {
      outcome: 'fail',
      method: props.method,
    });
    error.value = e.message || 'Invalid code. Please try again.';
    code.value = '';
  } finally {
    submitting.value = false;
  }
}

async function resend() {
  if (resendDisabled.value) return;
  resending.value = true;
  error.value = '';
  try {
    await issueCode();
    startResendCooldown();
  } catch (e) {
    error.value = e.message || 'Could not resend the code.';
  } finally {
    resending.value = false;
  }
}
</script>

<style scoped lang="scss">
@import '@/scss/variables.scss';
@import '@/scss/theme/variables.scss';

.alignment {
  text-align: -moz-center;
  text-align: -webkit-center;
}

.two-fa-verify {
  color: $black;

  .verify-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;
    padding-bottom: 1rem;
    margin-bottom: 1.25rem;
    border-bottom: 1px solid $grey-601;

    &__badge {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 46px;
      height: 46px;
      border-radius: 13px;
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
      color: #fff;
      font-size: 1.25rem;
      box-shadow: 0 6px 16px -4px rgba(99, 102, 241, 0.6);
    }

    &__title {
      font-size: 1.35rem;
      font-weight: 700;
      letter-spacing: -0.2px;
      color: $grey-2700;
      line-height: 1.2;
    }
  }

  .instruction {
    font-size: 0.95rem;
    color: $grey-2400;
  }

  .code-row {
    display: flex;
    justify-content: center;
  }

  .codeInput {
    width: 13rem;
    max-width: 100%;
    text-align: center;
    letter-spacing: 0.55rem;
    font-size: 1.5rem;
    font-weight: 600;
    // Fixed dark color: the input background is always light/white, so the
    // typed code must NOT use a theme variable that flips to light in dark mode
    // (that made the code invisible on the white field).
    color: #1f2937;
    border: 1.5px solid $grey-601;
    border-radius: 12px;
    padding: 0.7rem 0.5rem;
    background: #fff;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

    &::placeholder {
      color: #9ca3af;
      opacity: 1;
    }

    &:focus {
      outline: none;
      border-color: #6366f1;
      background: #fff;
      box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.16);
    }
  }

  .resend {
    font-size: 0.85rem;
    color: $grey-2400;

    .resend-link {
      color: $blue-500;
      cursor: pointer;
      text-decoration: underline;

      &.disabled {
        opacity: 0.6;
        cursor: default;
        text-decoration: none;
      }
    }
  }

  .error {
    color: $red-500;
    font-size: 0.85rem;
  }

  .createButton.btn-primary {
    min-width: 12rem;
    padding: 0.7rem 1.5rem;
    border: none;
    border-radius: 999px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: #fff;
    font-size: 0.95rem;
    letter-spacing: 0.2px;
    box-shadow: 0 6px 16px -4px rgba(99, 102, 241, 0.6);
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 10px 22px -6px rgba(99, 102, 241, 0.7);
      filter: brightness(1.04);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.5;
      box-shadow: none;
      cursor: not-allowed;
    }
  }
}
</style>
